import React from "react";
import { formatDayMonthWithWeekday } from "../../utils/formatDate";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

interface Row {
  label: string;
  editable: boolean;
  field: keyof DailyData;
}

interface WeekTableProps {
  week?: string;
  startDate: Date;
  rows: Row[];
  weekData: WeekData;
  onDataChange: (day: number, updatedDayData: DailyData) => void;
}

export interface DailyData {
  chambresVendues?: number;
  pourcentageVendues?: number;
  chambresAVendre?: number;
  tauxOccupation?: string;
}

export type WeekData = { [day: number]: DailyData };

const WeekTableForecast: React.FC<WeekTableProps> = ({
  week,
  startDate,
  rows,
  weekData,
  onDataChange,
}) => {
  const formattedDates = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      date,
      label: formatDayMonthWithWeekday(date, "short"),
    };
  });

  const handleDataChange = (
    dayIndex: number,
    field: keyof DailyData,
    value: number
  ) => {
    if (value < 0) {
      return;
    }

    const updatedDayData = {
      ...weekData[dayIndex],
      [field]: value,
    };

    if (
      updatedDayData.chambresVendues !== undefined &&
      updatedDayData.chambresAVendre !== undefined &&
      updatedDayData.chambresAVendre > 0
    ) {
      updatedDayData.pourcentageVendues = Math.round(
        (updatedDayData.chambresVendues / updatedDayData.chambresAVendre) *
          1000
      ) / 10;
      updatedDayData.tauxOccupation = `${updatedDayData.pourcentageVendues}%`;
    } else {
      updatedDayData.pourcentageVendues = undefined;
      updatedDayData.tauxOccupation = "-";
    }

    onDataChange(dayIndex, updatedDayData);
  };

  return (
    <Table sx={{ maxWidth: "90%", mt: 3, boxShadow: 3, borderRadius: 2 }}>
      {week && (
        <caption
          style={{
            captionSide: "top",
            textAlign: "left",
            fontSize: "1.25rem",
            color: "#bf2f38",
          }}
        >
          {week}
        </caption>
      )}
      <TableHead>
        <TableRow>
          <TableCell
            align="center"
            sx={{
              fontSize: "1rem",
              backgroundColor: "#f5f5f5",
              color: "#bf2f38",
            }}
          >
            Date
          </TableCell>
          {formattedDates.map(({ label }, i) => (
            <TableCell
              key={i}
              align="center"
              sx={{
                fontSize: "1rem",
                color: "#bf2f38",
                backgroundColor: "#fbeaec",
              }}
            >
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow
            key={rowIndex}
            sx={{
              backgroundColor: rowIndex % 2 === 0 ? "#fafafa" : "#fff",
            }}
          >
            <TableCell sx={{ whiteSpace: "normal", wordWrap: "break-word" }}>{row.label}</TableCell>
            {formattedDates.map((_, colIndex) => {
              const cellData = weekData[colIndex] || {};
              const value =
                row.editable && cellData[row.field] !== undefined
                  ? cellData[row.field]
                  : "";
              const isOverOccupied =
                row.field === "tauxOccupation" &&
                cellData.pourcentageVendues !== undefined &&
                cellData.pourcentageVendues > 100;

              return (
                <TableCell
                  key={colIndex}
                  align="center"
                  sx={{
                    backgroundColor: isOverOccupied ? "#ffebee" : "inherit",
                  }}
                >
                  {row.editable ? (
                    <TextField
                      variant="outlined"
                      size="small"
                      type="number"
                      value={value}
                      onChange={(e) =>
                        handleDataChange(
                          colIndex,
                          row.field,
                          Number(e.target.value)
                        )
                      }
                      inputProps={{ min: 0 }}
                      sx={{
                        width: "80px",
                        "& .MuiInputBase-input": {
                          textAlign: "center",
                          padding: "6px 4px",
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: isOverOccupied ? "red" : "#222222",
                      }}
                    >
                      {cellData[row.field] !== undefined
                        ? cellData[row.field]
                        : "-"}
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default WeekTableForecast;
